import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { CommonState } from "@obg/ngx.common";
import {
	CustomerManagementSelector,
	MessageAction,
	RESPONSIBLE_GAMING_MESSAGE_CONTEXT,
	SessionSelector,
} from "@obg/ngx.core";
import { CoreContentState, TranslationService } from "@obg/ngx.core.content";
import { DateFormat, DateService } from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { Observable } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { LoginUiAlertConfig } from "./login-ui-alert.config";
import { LastLoginDateAndTime } from "./login-ui.model";

@Injectable({ providedIn: "root" })
export class LoginUiAlertService {
	constructor(
		private store: Store<CoreContentState & CommonState>,
		private loginUiAlertConfig: LoginUiAlertConfig,
		private sessionSelector: SessionSelector,
		private translationService: TranslationService,
		private messageAction: MessageAction,
		private dateService: DateService
	) {}

	lastLoginTimestamp$(): Observable<string | undefined> {
		return this.store.pipe(
			select(this.sessionSelector.isAuthenticated()),
			filter((isAuth) => isAuth),
			switchMap(() => this.store.select(CustomerManagementSelector.getCustomer)),
			ignoreNil(),
			take(1),
			map((customer) => {
				this.store.dispatch(this.messageAction.clearByContext(RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert));

				return customer.lastLogins[1]?.timestamp;
			})
		);
	}

	getLoginDateAndTime$(): Observable<LastLoginDateAndTime> {
		return this.lastLoginTimestamp$().pipe(
			tap(() =>
				this.store.dispatch(this.messageAction.clearByContext(RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert))
			),
			map((timestamp) => {
				const isLastLoginDateAndTimeSecondsEnabled =
					this.loginUiAlertConfig.isLastLoginDateAndTimeSecondsEnabled();

				const lastLoginDateAndTime: LastLoginDateAndTime = {
					lastLoginDate: this.dateService.format(timestamp, DateFormat.LongDate),
					lastLoginTime: this.dateService.format(timestamp, DateFormat.ShortTime),
				};

				if (isLastLoginDateAndTimeSecondsEnabled) {
					lastLoginDateAndTime.lastLoginTime = this.dateService.format(timestamp, DateFormat.MediumTime);
				}

				return lastLoginDateAndTime;
			})
		);
	}

	dgojLoginAlertMessage$(): Observable<string | undefined> {
		return this.lastLoginTimestamp$().pipe(
			tap(() =>
				this.store.dispatch(this.messageAction.clearByContext(RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert))
			),
			map((timestamp) => {
				let message;
				if (timestamp) {
					message = this.translationService.getByKey("dgoj-login-alert.login-message", {
						lastLoginDate: this.dateService.format(timestamp, "HH:mm:ss MM/dd/yyyy z"),
					});
				}
				return message;
			})
		);
	}
}
